import { useTranslation } from 'gatsby-plugin-react-i18next';
import { graphql, useStaticQuery } from 'gatsby';

import Image from '../Atoms/Image';
import { FlyInText, OpacityWrapper } from '../Atoms/Animations';
import Link from '../Atoms/Link';
import ArrowButton from '../Atoms/ArrowButton';
import { BUYING_INDEX_ROUTE, SELLING_INDEX_ROUTE } from '../../utils/routes/const';

type BenefitsProps = {
  displayDescription?: boolean,
  titleClass?: string,
  type?: 'sell' | 'buy',
}

const imagesQuery = graphql`
query {
  scale: file(base: {eq: "scale.svg"}) {
    publicURL
  }
  package: file(base: {eq: "package.svg"}) {
    publicURL
  }
  customers: file(base: {eq: "customers.svg"}) {
    publicURL
  }
  wallet: file(base: {eq: "wallet.svg"}) {
    publicURL
  }
}
`

const benefitsSellData = [
  {
    icon: 'scale',
    title: 'benefits.0.title',
    description: 'benefits.0.description',
    url: `${SELLING_INDEX_ROUTE}#benefit-1`,
  },
  {
    icon: 'customers',
    title: 'benefits.1.title',
    description: 'benefits.1.description',
    url: `${SELLING_INDEX_ROUTE}#benefit-2`,
  },
  {
    icon: 'wallet',
    title: 'benefits.2.title',
    description: 'benefits.2.description',
    url: `${SELLING_INDEX_ROUTE}#benefit-3`,
  },
  {
    icon: 'wallet',
    title: 'benefits.3.title',
    description: 'benefits.3.description',
    url: `${SELLING_INDEX_ROUTE}#benefit-4`,
  },
]

const benefitsBuyData = [
  {
    icon: 'wallet',
    title: 'benefits.4.title',
    description: 'benefits.4.description',
    url: `${BUYING_INDEX_ROUTE}#benefit-1`,
  },
  {
    icon: 'package',
    title: 'benefits.5.title',
    description: 'benefits.5.description',
    url: `${BUYING_INDEX_ROUTE}#benefit-2`,
  },
  {
    icon: 'wallet',
    title: 'benefits.6.title',
    description: 'benefits.6.description',
    url: `${BUYING_INDEX_ROUTE}#benefit-3`,
  },
  {
    icon: 'wallet',
    title: 'benefits.7.title',
    description: 'benefits.7.description',
    url: `${BUYING_INDEX_ROUTE}#benefit-4`,
  }
]

const Benefits = ({
  displayDescription = true,
  titleClass = 'mt-12 font-cirka text-xl lg:text-4xl text-primary',
  type = 'sell'
}: BenefitsProps) => {
  const { t } = useTranslation();
  const data = useStaticQuery(imagesQuery);

  const benefitsData = type === 'sell' ? benefitsSellData : benefitsBuyData;
  const heading = type === 'sell' ? 'benefits.sell.heading' : 'benefits.buy.heading';

  return (
    <section className="py-12 px-4 lg:px-8">
      <div className="max-w-screen-2xl w-full mx-auto">
        <h2 className="font-cirka text-center pb-24 text-xl">{t(heading)}</h2>
        <dl className="grid lg:grid-cols-4 grid-cols-2 gap-8 lg:gap-24">
          {
            benefitsData.map((item) => (
              <OpacityWrapper
                key={`benefits-${item.title}`}
              >
                <dt>
                  <FlyInText
                    tag="div"
                    className="flex items-center ml-0 lg:mx-auto lg:h-40 w-18 lg:w-32"
                  >
                    <Image src={data[item.icon].publicURL} alt={item.icon} />
                  </FlyInText>

                  <FlyInText
                    tag="h3"
                    y="40px"
                    className={titleClass}
                  >
                    {t(item.title)}
                  </FlyInText>
                </dt>
                {displayDescription && (
                  <>
                    <FlyInText
                      tag="dd"
                      y="60px"
                      className="mt-8 mb-8 text-base font-light text-gray-700"
                      >
                      {t(item.description)}
                    </FlyInText>
                    <Link to={item.url}>
                      <ArrowButton isGhost={false}/>
                    </Link>
                  </>
                )}
              </OpacityWrapper>
            ))
          }
        </dl>
      </div>
    </section>
  );
}

export default Benefits;
